import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Eye, Users, TrendingUp, Inbox, ShoppingBag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const Card = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl border border-nosh-border bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="font-poster text-[11px] font-bold uppercase tracking-widest text-nosh-ink/50">{label}</span>
      <span className="grid h-9 w-9 place-items-center rounded-full bg-nosh-green/10 text-nosh-green"><Icon className="h-4 w-4" /></span>
    </div>
    <div className="mt-3 font-display text-3xl font-bold text-nosh-ink">{value}</div>
    {sub && <div className="mt-0.5 font-body text-xs text-nosh-ink/50">{sub}</div>}
  </div>
);

export default function AnalyticsTab() {
  const { API, authHeaders, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { headers: authHeaders });
      setData(data);
    } catch (e) { if (e.response?.status === 401) { toast.error("Session expired"); logout(); } }
    finally { setLoading(false); }
  }, [API, authHeaders, logout]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !data) return <div className="grid place-items-center rounded-2xl border border-nosh-border bg-white py-20"><Loader2 className="h-6 w-6 animate-spin text-nosh-green" /></div>;

  const maxDay = Math.max(1, ...data.visits_7d.map((d) => d.count));

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card icon={Eye} label="Site visits" value={data.total_visits} sub="All page views" testid="stat-visits" />
        <Card icon={Users} label="Unique visitors" value={data.unique_visitors} sub="By session" testid="stat-unique" />
        <Card icon={Inbox} label="Inquiries" value={data.total_inquiries} sub="Requests received" testid="stat-inquiries" />
        <Card icon={TrendingUp} label="Conversion" value={`${data.conversion_rate}%`} sub="Visitors → inquiry" testid="stat-conversion" />
      </div>

      <div className="rounded-2xl border border-nosh-border bg-white p-6" data-testid="visits-chart">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-nosh-ink">Visits — last 7 days</h3>
          <span className="font-body text-sm text-nosh-ink/50">{data.visits_7d.reduce((s, d) => s + d.count, 0)} total</span>
        </div>
        <div className="flex h-40 items-end gap-3">
          {data.visits_7d.map((d) => (
            <div key={d.date} className="flex flex-1 flex-col items-center gap-2">
              <div className="flex w-full flex-1 items-end">
                <div className="w-full rounded-t-lg bg-nosh-green transition-all" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "6px" : "2px", opacity: d.count ? 1 : 0.25 }} title={`${d.count} visits`} />
              </div>
              <span className="font-poster text-[11px] font-bold text-nosh-ink/45">{d.count}</span>
              <span className="font-poster text-[10px] uppercase tracking-wide text-nosh-ink/40">{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card icon={ShoppingBag} label="Carts w/ items" value={data.carts_with_items} testid="stat-carts" />
        <Card icon={ShoppingBag} label="Abandoned carts" value={data.abandoned_carts} sub="Added but no request" testid="stat-abandoned" />
        <Card icon={Users} label="Leads captured" value={data.leads} sub="Left contact info" testid="stat-leads" />
        <Card icon={TrendingUp} label="Converted carts" value={data.converted_carts} sub="Became inquiries" testid="stat-converted" />
      </div>
    </div>
  );
}
